import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { CandidateDetail } from "../../../../models/candidate-detail.model";
import { SectionBlock, Title } from "../../talent.styled";
import { ShowMore, Skill, SkillsList } from "./candidate-skills.styled";
import { Text } from "../../../common/text/text";
import { useTheme } from "styled-components";
import { FaMinusCircle, FaPlusCircle } from "react-icons/fa";

interface CandidateSkillsProps {
  candidate: CandidateDetail;
}

const CandidateSkills: React.FC<CandidateSkillsProps> = ({ candidate }) => {
  const [
    isShowAllCertifiedSkills,
    setIsShowAllCertifiedSkills,
  ] = useState<boolean>(false);
  const [
    isShowAllNotCertifiedSkills,
    setIsShowAllNotCertifiedSkills,
  ] = useState<boolean>(false);
  const theme = useTheme();
  const { t } = useTranslation("private-home-page");

  const skillsCertified = useMemo(
    () => candidate.skills?.filter((skill) => skill.is_certified),
    [candidate.skills]
  );

  const skillsNotCertified = useMemo(
    () => candidate.skills?.filter((skill) => !skill.is_certified),
    [candidate.skills]
  );

  const renderSkills = (skills: any[], open: boolean) => {
    const filteredSkills = !open ? skills.slice(0, 2) : skills;
    return filteredSkills.map((skill) => (
      <Skill key={skill.name}>{skill.name}</Skill>
    ));
  };

  const renderShowMore = (open: boolean) => {
    if (open) {
      return (
        <>
          {t("talent.showLess")}
          <FaMinusCircle />
        </>
      );
    } else {
      return (
        <>
          {t("talent.showMore")}
          <FaPlusCircle />
        </>
      );
    }
  };

  const renderCertifiedSkills = useMemo(() => {
    return renderSkills(skillsCertified, isShowAllCertifiedSkills);
  }, [skillsCertified, isShowAllCertifiedSkills]);

  const renderNotCertifiedSkills = useMemo(() => {
    return renderSkills(skillsNotCertified, isShowAllNotCertifiedSkills);
  }, [skillsNotCertified, isShowAllNotCertifiedSkills]);

  return (
    <SectionBlock>
      <Title>
        {t("talent.habilities")} <span>({candidate.skills.length})</span>
      </Title>
      {renderCertifiedSkills.length > 0 && (
        <>
          <Text as="h5" margin=".8rem 0" color={theme.colors.primaryBlue.main}>
            {t("talent.certifiedHabilities")}:
          </Text>
          <SkillsList>
            {renderCertifiedSkills?.map((skill) => skill)}
          </SkillsList>
          <ShowMore
            onClick={() => {
              setIsShowAllCertifiedSkills(!isShowAllCertifiedSkills);
            }}
          >
            {renderShowMore(isShowAllCertifiedSkills)}
          </ShowMore>
        </>
      )}

      {renderNotCertifiedSkills.length > 0 && (
        <>
          <Text as="h5" margin=".8rem 0" color={theme.colors.primaryBlue.main}>
            {t("talent.notCertifiedHabilities")}:
          </Text>
          <SkillsList>
            {renderNotCertifiedSkills?.map((skill) => skill)}
          </SkillsList>
          <ShowMore
            onClick={() => {
              setIsShowAllNotCertifiedSkills(!isShowAllNotCertifiedSkills);
            }}
          >
            {renderShowMore(isShowAllNotCertifiedSkills)}
          </ShowMore>
        </>
      )}
    </SectionBlock>
  );
};

export default CandidateSkills;
